import { compareAsc, differenceInDays, differenceInHours, format, isToday, isTomorrow } from 'date-fns';
import { BillingScheduleCollectionResponse } from '@acorn-portal/shared/src/services/managedPaymentsApi';
import { useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import { useTheme, Box, Typography } from '@mui/material';
import PaymentScheduleTable from '@acorn-portal/shared/src/components/display/payment-schedule-table';

type SplitPaymentStatusOfPayment = 'Active' | 'PaidOff' | 'NoPlan';

export const usePaymentSchedule = (collections: BillingScheduleCollectionResponse[]) => {
	const theme = useTheme();
	const payNowDetails = useAppSelector((state) => state.payNow);
	const missedPaymentCollectionReference = payNowDetails.details?.missedPaymentDetails?.iceCollectionReference;

	const sortedRows = [...collections].sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime());

	const formatDate = (rowData: BillingScheduleCollectionResponse): string => {
		const scheduledDate = new Date(rowData.scheduledDate);
		const today = new Date();

		if (isToday(scheduledDate)) {
			return 'Due Today';
		}

		if (isTomorrow(scheduledDate)) {
			return 'Due Tomorrow';
		}

		if (rowData.overdue) {
			return `Was due on ${format(scheduledDate, 'EEE d MMMM yyyy')}`;
		}

		const diffDays = differenceInDays(scheduledDate, today);
		const diffHours = differenceInHours(scheduledDate, today);
		// Make it more human understandable.
		const diffDaysRounded = diffHours > diffDays * 24 ? diffDays + 1 : diffDays;
		if (diffDaysRounded > 0 && diffDaysRounded <= 30) {
			return `Due in ${diffDaysRounded} days`;
		}

		return format(scheduledDate, 'EEE d MMMM yyyy');
	};

	const getDescription = (rowData: BillingScheduleCollectionResponse) => {
		if (rowData.paymentMethod?.settlementMethodCode === 'CARD' && rowData.overdue) return 'Overdue Direct Debit';
		if (rowData.paymentMethod?.settlementMethodCode === 'DDM') return 'Direct Debit';

		if (
			(rowData.paymentMethod?.settlementMethodCode === 'CARD' ||
				rowData.paymentMethod?.settlementMethodCode === 'MSP') &&
			rowData === sortedRows[0]
		)
			return 'Initial Card Payment';

		if (rowData.paymentMethod?.settlementMethodCode === 'CARD' || rowData.paymentMethod?.settlementMethodCode === 'MSP')
			return 'Scheduled Card Payment';
	};

	const getTextStyle = (collection: BillingScheduleCollectionResponse) => {
		const payNowStatus = getStatusOfSplitPaymentForThisPayment(collection);
		switch (true) {
			case collection.status === 'AUTHORISED':
			case collection.status === 'SETTLED':
			case collection.status === 'CONSOLIDATED':
			case collection.status === 'FAILED_AND_REPLACED' && (payNowStatus === 'NoPlan' || payNowStatus === 'PaidOff'):
				return { color: theme.palette.grey[500] };
			case collection.status === 'OVERDUE':
				return { color: theme.palette.error.main };
			default:
				return {}; // No additional styles
		}
	};

	const getStatusText = (collection: BillingScheduleCollectionResponse) => {
		const payNowStatus = getStatusOfSplitPaymentForThisPayment(collection);

		switch (true) {
			case collection.status === 'AUTHORISED':
			case collection.status === 'SETTLED':
			case collection.status === 'CONSOLIDATED':
			case collection.status === 'FAILED_AND_REPLACED' && (payNowStatus === 'NoPlan' || payNowStatus === 'PaidOff'):
				return 'Payment Taken';
			case collection.status === 'REQUESTED':
				return 'Pending';
			case collection.status === 'OVERDUE':
				return 'Payment Overdue';
			case collection.status === 'FAILED':
				return 'Payment Failed';
			case collection.status === 'FAILED_AND_REPLACED':
				return 'Payment Plan';
			case collection.status === 'SCHEDULED':
				return 'Payment Scheduled';
			case collection.status === 'CANCELLED':
				return 'Payment Cancelled';
			case collection.status === 'SUSPENDED':
				return 'Payment Suspended';
		}
	};

	const getPayNowPaymentSchedule = (collection: BillingScheduleCollectionResponse) => {
		const splitPaymentPlans = payNowDetails?.details?.splitPaymentPlans;
		if (!splitPaymentPlans?.length || splitPaymentPlans?.length < 1) return null;

		const status = getStatusOfSplitPaymentForThisPayment(collection);
		if (status !== 'Active' || missedPaymentCollectionReference !== collection.id) return null;

		return (
			<Box>
				<Typography variant='subtitle1' color='secondary' sx={{ fontSize: '1.1rem', my: 1 }}>
					Direct Debit Payment Plan
				</Typography>
				<PaymentScheduleTable splitPaymentPlans={splitPaymentPlans} />
			</Box>
		);
	};

	const getStatusOfSplitPaymentForThisPayment = (
		collection: BillingScheduleCollectionResponse,
	): SplitPaymentStatusOfPayment => {
		const missedPaymentCollectionReference = payNowDetails.details?.missedPaymentDetails?.iceCollectionReference;
		const missedPaymentDate = payNowDetails?.details?.missedPaymentDetails?.missedPaymentDate;
		if (!missedPaymentDate) {
			return 'NoPlan';
		}

		const plans = payNowDetails.details?.splitPaymentPlans;
		if (plans && plans.length > 0) {
			const allPaid =
				plans.every((plan) => plan.status === 'Paid') || missedPaymentCollectionReference !== collection.id;
			if (allPaid) {
				return 'PaidOff';
			} else {
				return 'Active';
			}
		}

		const comparisonResult = compareAsc(new Date(missedPaymentDate), new Date(collection.dueDate));

		if (comparisonResult < 0) {
			return 'PaidOff';
		}

		return 'NoPlan';
	};

	return {
		sortedRows,
		formatDate,
		getTextStyle,
		getDescription,
		getStatusText,
		getPayNowPaymentSchedule,
		getStatusOfSplitPaymentForThisPayment,
	};
};
